import { utils } from "@/utils/utils";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";

@Component({
  name: "b-form-input-datepicker",
  components: { DatePicker },
})
export default class FormInputDatepickerComponent extends Vue {
  @Prop() public formType!: string;
  @Prop() public editForm!: boolean;
  @Prop() public keyName!: string;
  @Prop() public value!: string;
  @Prop() public payloadData: any;
  @Prop() public isPreviewForm!: boolean;
  @Prop({ default: null }) public minAge!: number | null;
  @Prop({ default: true }) public disableFuture!: boolean;

  public isDisable = false;
  public displayDate: string | null = null;
  public validationError = "";
  private hasInputError = false;
  private rawInput = "";
  private skipNextWatch = false;

  get inputClass(): string {
    return (
      "date-input form-control" + (this.validationError ? " is-invalid" : "")
    );
  }

  public created() {
    this.isDisable = utils.disableField(this.editForm, this.formType);
    this.skipNextWatch = true;
    this.displayDate = this.toDisplayDate(this.value);
  }

  public mounted() {
    const pickerEl = (this.$refs.datePicker as any)?.$el;
    if (pickerEl) {
      pickerEl.addEventListener(
        "input",
        (e: Event) => {
          this.rawInput = (e.target as HTMLInputElement).value;
        },
        true // capture phase — fires before datepicker's own listener
      );
    }
  }

  public onCalendarOpen() {
    this.rawInput = "";
  }

  public onPickerBlur() {
    if (this.validationError && this.rawInput) {
      const typed = this.rawInput;
      this.$nextTick(() => {
        const picker = this.$refs.datePicker as any;
        if (picker) picker.userInput = typed;
      });
    }
  }

  public disableFutureDates(date: Date): boolean {
    if (!this.disableFuture) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date > today;
  }

  public disabledCalendarChanger(date: Date, type: string): boolean {
    if (!this.disableFuture) return false;
    if (type === "year") {
      return date.getFullYear() > new Date().getFullYear();
    }
    return false;
  }

  public onDateInput(value: string | null) {
    if (!value) return;
    if (this.rawInput && this.rawInput !== value) {
      const rawError = this.validateDate(this.rawInput);
      this.validationError =
        rawError || "Please enter a valid date in MM-DD-YYYY format.";
      const typed = this.rawInput;
      this.$nextTick(() => {
        const picker = this.$refs.datePicker as any;
        if (picker) picker.userInput = typed;
      });
      return;
    }
    this.rawInput = "";
    this.validationError = this.validateDate(value);
    if (!this.validationError) {
      this.skipNextWatch = true;
      this.$emit("input", this.toISODate(value));
    }
  }

  public onInputError(rawValue: string) {
    if (rawValue) {
      this.rawInput = rawValue;
      this.hasInputError = true;
      this.validationError = this.validateDate(rawValue);
      this.$nextTick(() => {
        const picker = this.$refs.datePicker as any;
        if (picker) picker.userInput = rawValue;
      });
    }
  }

  @Watch("displayDate")
  onDisplayDateChange() {
    if (this.skipNextWatch) {
      this.skipNextWatch = false;
      return;
    }
    if (this.hasInputError) {
      this.hasInputError = false;
    }
  }

  @Watch("value")
  onValueChange(newVal: string) {
    this.rawInput = "";
    this.validationError = "";
    this.skipNextWatch = true;
    this.displayDate = this.toDisplayDate(newVal);
  }

  private validateDate(val: string | null): string {
    if (!val) return "";
    const parts = val.split("-");
    if (
      parts.length !== 3 ||
      parts[0].length !== 2 ||
      parts[1].length !== 2 ||
      parts[2].length !== 4
    ) {
      return "Please enter a valid date in MM-DD-YYYY format.";
    }
    const month = parseInt(parts[0], 10);
    const day = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    const date = new Date(year, month - 1, day);
    if (
      date.getMonth() !== month - 1 ||
      date.getDate() !== day ||
      date.getFullYear() !== year
    ) {
      return "Please enter a valid date.";
    }
    if (this.disableFuture && date > new Date()) {
      return "Future dates are not allowed.";
    }
    return "";
  }

  private toDisplayDate(val: string): string | null {
    if (!val) return null;
    const parts = val.split("-");
    // Convert YYYY-MM-DD (ISO from API) → MM-DD-YYYY (display)
    if (parts.length === 3 && parts[0].length === 4) {
      return `${parts[1]}-${parts[2]}-${parts[0]}`;
    }
    return val;
  }

  private toISODate(val: string | null): string {
    if (!val) return "";
    const parts = val.split("-");
    // Convert MM-DD-YYYY (display) → YYYY-MM-DD (ISO for API/payload)
    if (parts.length === 3 && parts[2].length === 4) {
      return `${parts[2]}-${parts[0]}-${parts[1]}`;
    }
    return val;
  }
}
